import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings, GitCompare } from "lucide-react";

export default function CompressionSettings({
  quality,
  setQuality,
  format,
  setFormat,
  maxWidth,
  setMaxWidth,
  maxHeight,
  setMaxHeight,
  compressionPreset,
  setCompressionPreset,
  removeExif,
  setRemoveExif,
  exifOptions,
  setExifOptions,
  comparisonMode,
  onToggleComparisonMode
}) {
  return (
    <Card className="control-panel border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-xl transition-colors duration-300">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Compression Settings</h3>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Comparison Mode</label>
            <Switch
              checked={comparisonMode}
              onCheckedChange={onToggleComparisonMode}
              data-testid="comparison-toggle"
            />
            <GitCompare className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          </div>
        </div>

        {!comparisonMode && (
          <div className="space-y-6">
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">Compression Preset</label>
              <Select value={compressionPreset} onValueChange={setCompressionPreset}>
                <SelectTrigger data-testid="preset-select" className="bg-slate-50 text-slate-900 dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="quality">High Quality (Larger file)</SelectItem>
                  <SelectItem value="balanced">Balanced (Recommended)</SelectItem>
                  <SelectItem value="maximum">Maximum Compression (Smallest file)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {compressionPreset === "maximum" && "Maximum savings - ~75% size reduction, auto-resize to 1920px"}
                {compressionPreset === "balanced" && "Smart compression - ~55% size reduction, optimal quality/size (recommended)"}
                {compressionPreset === "quality" && "High quality - ~20% size reduction, preserves fine details"}
              </p>
            </div>

            <Separator className="dark:bg-slate-700" />

            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Quality</label>
                <Badge variant="secondary" data-testid="quality-value" className="dark:bg-slate-700 dark:text-slate-200">{quality[0]}%</Badge>
              </div>
              <Slider
                data-testid="quality-slider"
                value={quality}
                onValueChange={setQuality}
                min={1}
                max={100}
                step={1}
                className="w-full"
              />
            </div>

            <Separator className="dark:bg-slate-700" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">Format</label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="format-select" className="bg-slate-50 text-slate-900 dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="jpeg">JPEG/JPG</SelectItem>
                    <SelectItem value="png">PNG</SelectItem>
                    <SelectItem value="webp">WebP</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">Max Width (px)</label>
                <Slider
                  data-testid="width-slider"
                  value={maxWidth}
                  onValueChange={setMaxWidth}
                  min={0}
                  max={4000}
                  step={100}
                  className="w-full"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{maxWidth[0] === 0 ? "Original" : maxWidth[0]}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block">Max Height (px)</label>
                <Slider
                  data-testid="height-slider"
                  value={maxHeight}
                  onValueChange={setMaxHeight}
                  min={0}
                  max={4000}
                  step={100}
                  className="w-full"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{maxHeight[0] === 0 ? "Original" : maxHeight[0]}</p>
              </div>
            </div>

            <Separator className="dark:bg-slate-700" />

            {/* EXIF Metadata Removal */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Remove EXIF Metadata</label>
                <Switch
                  checked={removeExif}
                  onCheckedChange={setRemoveExif}
                  data-testid="exif-toggle"
                />
              </div>
              {removeExif && (
                <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 transition-colors duration-300">
                  <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">Select data to remove:</p>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="camera"
                      checked={exifOptions.camera}
                      onCheckedChange={(checked) => setExifOptions({...exifOptions, camera: checked})}
                    />
                    <label htmlFor="camera" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                      Camera Info (Model, Make, Lens)
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="gps"
                      checked={exifOptions.gps}
                      onCheckedChange={(checked) => setExifOptions({...exifOptions, gps: checked})}
                    />
                    <label htmlFor="gps" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                      GPS Location (Latitude, Longitude)
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="device"
                      checked={exifOptions.device}
                      onCheckedChange={(checked) => setExifOptions({...exifOptions, device: checked})}
                    />
                    <label htmlFor="device" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                      Device Data (Software, Date/Time)
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {comparisonMode && (
          <div className="text-center py-4">
            <p className="text-slate-600 dark:text-slate-400">
              <GitCompare className="w-5 h-5 inline mr-2" />
              Viewing all presets side-by-side. Toggle off to customize settings.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
